import React from 'react';
import './style.css';
import {Space, Table, TableProps as AntTableProps} from 'antd';
import {ColumnsType} from 'antd/es/table';
import {DeleteOutlined, EditOutlined, EyeOutlined} from '@ant-design/icons';
import {NoDataPlaceholder} from "../../../utils/helpers";

export type TableComponentProps<T> = {
    columns: ColumnsType<T>;
    data: T[];
    title?: string;
    onEdit?: (record: T) => void;
    onDelete?: (record: T) => void;
    onView?: (record: T) => void;
    onRowClick?: (record: T) => void;
    loading?: boolean;
    onChange?: AntTableProps<T>['onChange'];

    isMobile?: boolean;
    mobileVariant?: string;
    mobileRenderers?: Record<string, (record: T) => React.ReactNode>;
    rowKey?: (record: T) => string;
};

/**
 * TableComponent
 *
 * A reusable table component with row-click handling and action icons.
 *
 * @param {ColumnsType<T>} columns - Table columns.
 * @param {T[]} data - Data records.
 * @param {string} [title] - Table title.
 * @param {function} [onEdit] - Callback for edit action.
 * @param {function} [onDelete] - Callback for delete action.
 * @param {function} [onView] - Callback for view action.
 * @param {function} [onRowClick] - Callback for row click action.
 * @param loading
 * @param onChange
 * @param isMobile
 * @param mobileVariant
 * @param mobileRenderers
 * @param rowKey
 */
const TableComponent = <T extends Record<string, any>>({
                                                           columns,
                                                           data = [],
                                                           title,
                                                           onEdit,
                                                           onDelete,
                                                           onView,
                                                           onRowClick,
                                                           loading,
                                                           onChange,
                                                           isMobile,
                                                           mobileVariant,
                                                           mobileRenderers,
                                                           rowKey,
                                                       }: TableComponentProps<T>) => {
    const handleEdit = (rec: T) => onEdit?.(rec);
    const handleDelete = (rec: T) => onDelete?.(rec);
    const handleView = (rec: T) => onView?.(rec);
    const handleRowClick = (rec: T) => onRowClick?.(rec);

    const hasActions = onEdit || onDelete || onView;
    const actionColumn: ColumnsType<T> = hasActions ? [
        {
            title: 'Action',
            key: 'operation',
            fixed: 'right',
            width: 120,
            render: (_: any, record: T) => (
                <Space>
                    {onEdit && <EditOutlined onClick={() => handleEdit(record)}/>}
                    {onDelete && <DeleteOutlined onClick={() => handleDelete(record)}/>}
                    {onView && <EyeOutlined onClick={() => handleView(record)}/>}
                </Space>
            ),
        },
    ] : [];

    const getKey = (item: T) => {
        if (rowKey) return rowKey(item);
        return item._id || item.id || item.key;
    };

    const mobile = Boolean(isMobile);

    const renderer = (mobileVariant && mobileRenderers?.[mobileVariant]) ? mobileRenderers[mobileVariant] : undefined;

    if (mobile && renderer) {
        if (mobile && renderer) {
            return (
                <div>
                    {title && <label className="title">{title}</label>}

                    {(!data || data.length === 0) ? (
                        <div style={{padding: '40px 0'}}>
                            <NoDataPlaceholder message="No data"/>
                        </div>
                    ) : (
                        <div className="mobileList">
                            {(data || []).map((item) => (
                                <div
                                    key={getKey(item)}
                                    className="mobileCard"
                                    onClick={() => onRowClick?.(item)}
                                    style={onRowClick ? {cursor: 'pointer'} : {}}
                                >
                                    {renderer(item)}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            );
        }
    }

    return (
        <div>
            {title && <label className="title">{title}</label>}
            <Table<T> loading={loading} onChange={onChange} columns={[...columns, ...actionColumn]}
                      dataSource={data.map(item => ({...item, key: item.id}))} scroll={{x: 'max-content', y: 600}}
                      onRow={record => ({
                          onClick: () => handleRowClick(record),
                          style: onRowClick ? {cursor: 'pointer'} : {},
                      })}
            />
        </div>
    );
};

export default TableComponent;